import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { InvestigadorRecord } from '../types';

interface Props {
  records: InvestigadorRecord[];
}

const COLORES_BD = [
  '#0D3157',
  '#1d6fa8',
  '#2a9d8f',
  '#e76f51',
  '#f4a261',
  '#264653',
  '#a8dadc',
  '#457b9d',
  '#e63946',
  '#606c38',
];

export default function GraficasAdmin({ records }: Props) {
  if (records.length === 0) return null;

  /* ——— Datos: publicaciones por tipo ——— */
  const totalDocentes = records
    .filter((r) => r.tipoUsuario === 'docente')
    .reduce((acc, r) => acc + r.publicaciones.length, 0);

  const totalEstudiantes = records
    .filter((r) => r.tipoUsuario === 'estudiante')
    .reduce((acc, r) => acc + r.publicaciones.length, 0);

  const dataUsuario = [
    { nombre: 'Docentes', publicaciones: totalDocentes, color: '#0D3157' },
    { nombre: 'Estudiantes', publicaciones: totalEstudiantes, color: '#f4a261' },
  ];

  /* ——— Datos: publicaciones por base de datos ——— */
  const bdCount: Record<string, number> = {};

  records.forEach((r) => {
    r.publicaciones.forEach((p) => {
      if (!p.baseDatos) {
        bdCount['Sin especificar'] = (bdCount['Sin especificar'] ?? 0) + 1;
        return;
      }
      const label =
        p.baseDatos === 'Otras (especificar)' && p.baseDatosOtra
          ? p.baseDatosOtra
          : p.baseDatos;
      bdCount[label] = (bdCount[label] ?? 0) + 1;
    });
  });

  const dataBD = Object.entries(bdCount)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  /* ——— Datos: investigadores por tipo ——— */
  const pieTipoData = [
    { name: 'Docentes', value: records.filter((r) => r.tipoUsuario === 'docente').length },
    { name: 'Estudiantes', value: records.filter((r) => r.tipoUsuario === 'estudiante').length },
  ].filter((d) => d.value > 0);

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-5 bg-[#0D3157] rounded-full" />
        <h3 className="text-[#0D3157] font-bold text-base">Estadísticas y Gráficas</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Gráfica 1: publicaciones por tipo de usuario */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm font-bold text-slate-700 mb-1">
            Publicaciones por tipo de usuario
          </p>
          <p className="text-xs text-slate-400 mb-4">Total de publicaciones registradas</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dataUsuario} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="nombre" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip
                formatter={(v: number) => [`${v} publicaciones`, '']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Bar dataKey="publicaciones" radius={[6, 6, 0, 0]}>
                {dataUsuario.map((entry) => (
                  <Cell key={entry.nombre} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfica 2: distribución docentes vs estudiantes (registros) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm font-bold text-slate-700 mb-1">
            Investigadores registrados
          </p>
          <p className="text-xs text-slate-400 mb-4">Proporción de docentes y estudiantes</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieTipoData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }: { name: string; percent: number }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                labelLine={false}
              >
                {pieTipoData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={i === 0 ? '#0D3157' : '#f4a261'}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number) => [`${v} registros`, '']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfica 3: publicaciones por base de datos (ancho completo) */}
        {dataBD.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 md:col-span-2">
            <p className="text-sm font-bold text-slate-700 mb-1">
              Publicaciones por base de datos indexada
            </p>
            <p className="text-xs text-slate-400 mb-4">
              Distribución según Latindex, SciELO, Scopus, WOS y otras
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={dataBD}
                barSize={32}
                margin={{ left: 10, right: 10, top: 5, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip
                  formatter={(v: number) => [`${v} publicaciones`, '']}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                />
                <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                  {dataBD.map((_, i) => (
                    <Cell key={i} fill={COLORES_BD[i % COLORES_BD.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
